import { create } from 'zustand'

interface User {
  accessToken: string
  refreshToken: string
  isAuthenticated: boolean
}

interface AppStore {
  user: User
  setSession: (accessToken: string, refreshToken: string) => void
  setGatewaySession: () => void
  setAccessToken: (token: string) => void
  logout: () => void
  devMode: boolean
  setDevMode: (enabled: boolean) => void
  sidebarCollapsed: boolean
  setSidebarCollapsed: (collapsed: boolean) => void
  // Title of the RFP being viewed, so the top bar can name it instead of its id.
  currentRfpTitle: string
  setCurrentRfpTitle: (title: string) => void
}

const readDevMode = (): boolean => {
  try { return localStorage.getItem('bp_dev_mode') === '1' } catch { return false }
}

// Collapsed unless the user expanded it. New key: the old one stored '0' for everyone.
const readSidebarCollapsed = (): boolean => {
  try { return localStorage.getItem('bp_sidebar_expanded') !== '1' } catch { return true }
}

export const useAppStore = create<AppStore>((set) => ({
  user: {
    accessToken: localStorage.getItem('accessToken') || '',
    refreshToken: localStorage.getItem('refreshToken') || '',
    isAuthenticated: !!(localStorage.getItem('accessToken') || localStorage.getItem('gatewaySession')),
  },
  setSession: (accessToken: string, refreshToken: string) => {
    localStorage.setItem('accessToken', accessToken)
    localStorage.setItem('refreshToken', refreshToken)
    set({
      user: {
        accessToken,
        refreshToken,
        isAuthenticated: true,
      },
    })
  },
  setGatewaySession: () => {
    localStorage.setItem('gatewaySession', '1')
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    set({
      user: {
        accessToken: '',
        refreshToken: '',
        isAuthenticated: true,
      },
    })
  },
  setAccessToken: (token: string) => {
    localStorage.setItem('accessToken', token)
    set({
      user: {
        accessToken: token,
        refreshToken: localStorage.getItem('refreshToken') || '',
        isAuthenticated: true,
      },
    })
  },
  devMode: readDevMode(),
  setDevMode: (enabled: boolean) => {
    try { localStorage.setItem('bp_dev_mode', enabled ? '1' : '0') } catch { /* ignore */ }
    set({ devMode: enabled })
  },
  sidebarCollapsed: readSidebarCollapsed(),
  setSidebarCollapsed: (collapsed: boolean) => {
    try { localStorage.setItem('bp_sidebar_expanded', collapsed ? '0' : '1') } catch { /* ignore */ }
    set({ sidebarCollapsed: collapsed })
  },
  currentRfpTitle: '',
  setCurrentRfpTitle: (title: string) => set({ currentRfpTitle: title }),
  logout: () => {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('gatewaySession')
    set({
      user: {
        accessToken: '',
        refreshToken: '',
        isAuthenticated: false,
      },
    })
  },
}))
